import { DropDown, Input, useErrorState } from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { isRepeat } from 'axios/clientApi';
import { getSelectList } from 'axios/dictionaryApi';
import { changeOrderSpecialMark } from 'axios/orderApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { Dictionary, groupBy, isEqual } from 'lodash';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { OrderChecksInfo, OrderPvChecks, OrderScoringHQueueParam } from 'models/api/order';
import { useOrderInfoContext } from 'providers/OrderInfoProvider';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Col, Row } from 'reactstrap';
import i18n from 'utils/i18n';
import GetOptions from 'utils/options';

type UseChecksTabHelperProps = {
    isOpen: boolean;
    checksInfoQueryFn: () => Promise<OrderChecksInfo>;
    orderId: number;
    clientId: number;
    getHqueuesCallback: (hqueues: Dictionary<Dictionary<OrderScoringHQueueParam>>) => {
        hqueueTotal: Dictionary<OrderScoringHQueueParam>;
    } & Record<string, Dictionary<OrderScoringHQueueParam>>;
};

const useChecksTabHelper = ({
    isOpen,
    checksInfoQueryFn,
    orderId,
    clientId,
    getHqueuesCallback,
}: UseChecksTabHelperProps) => {
    const api = useApi();
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();
    const [checksInfo, setChecksInfo] = useState<OrderChecksInfo | null>(null);
    const { scoring, setScoring, setDirty, orderDetails } = useOrderInfoContext();

    const [wasOpened, setWasOpened] = useState(false);

    const { data: isRepeatClient, isFetching: isRepeatClientFetching } = useQuery(
        'isRepeatClient',
        () =>
            orderDetails?.orderGeneral?.clientId
                ? isRepeat(api, orderDetails.orderGeneral.clientId)
                : Promise.resolve(false),
        {
            enabled: wasOpened,
            onError: (err) => {
                handleRequestError(err);
            },
        }
    );

    const { data: checkTabDictInfo, isFetching: dictsFetching } = useQuery(
        'checkTabDicts',
        () =>
            getSelectList(
                api,
                [
                    'PvTotalCheckResults',
                    'ReasonsForDeclined',
                    'ReasonsForDeclinedWrongInformation',
                    'ReasonsForDeclinedForTime',
                    'ReasonsForDeclinedPhotos',
                    'ReasonsForDeclinedUnmatchedTransunion',
                    'ReasonsForDeclinedPossibleFraud',
                    'ReasonsForDeclinedCustomerRequest',
                    'ReasonsForApprove',
                    'ReasonsForApproveMakeMovements',
                    'ReasonsForApproveSecurity',
                    'ReasonsForApprovePayroll',
                    'ReasonsForApproveNewBankBenefits',
                    'ReasonsForApproveCost',
                ],
                i18n.language
            ),
        {
            enabled: wasOpened,
            onError: (err) => {
                handleRequestError(err);
            },
        }
    );

    const { isFetching: checksInfoFetching } = useQuery('checksInfo', () => checksInfoQueryFn(), {
        onSuccess: (data) => {
            setChecksInfo(data);
            setScoring(data.scoring);
        },
        enabled: wasOpened,
        onError: (err) => {
            handleRequestError(err);
        },
    });

    const specialMarkMutation = useMutation(
        'specialMarkMutation',
        () =>
            changeOrderSpecialMark(api, {
                orderId: orderId,
                clientId: clientId,
                specialMark: scoring?.specialMark ?? false,
                comment: scoring?.specialMarkComment ?? null,
            }),
        {
            onSuccess: () =>
                setChecksInfo((prev) =>
                    prev
                        ? {
                              ...prev,
                              scoring: {
                                  ...prev.scoring,
                                  specialMark: scoring?.specialMark ?? false,
                                  specialMarkComment: scoring?.specialMarkComment ?? null,
                              },
                          }
                        : prev
                ),
            onError: (err: AxiosError<AxiosErrorResponse>) => {
                handleRequestError(err);
            },
        }
    );

    const pvTotalCheckDict = useMemo(
        () => (checkTabDictInfo && checkTabDictInfo['PvTotalCheckResults']) || [],
        [checkTabDictInfo]
    );

    const reasonsForDeclinedDict = useMemo(
        () => (checkTabDictInfo && checkTabDictInfo['ReasonsForDeclined']) || [],
        [checkTabDictInfo]
    );

    const reasonsForDeclinedLvl2Dict = useMemo(
        () =>
            (checkTabDictInfo &&
                scoring?.totalCheck?.rejectReason &&
                checkTabDictInfo['ReasonsForDeclined' + scoring?.totalCheck?.rejectReason]) ||
            [],
        [checkTabDictInfo, scoring]
    );

    const reasonsForApproveDict = useMemo(
        () => (checkTabDictInfo && checkTabDictInfo['ReasonsForApprove']) || [],
        [checkTabDictInfo]
    );

    const reasonsForApproveLvl2Dict = useMemo(
        () =>
            (checkTabDictInfo &&
                scoring?.totalCheck?.approveReason &&
                checkTabDictInfo['ReasonsForApprove' + scoring?.totalCheck?.approveReason]) ||
            [],
        [checkTabDictInfo, scoring]
    );

    const hqueues = useMemo(() => scoring && getHqueuesCallback(scoring.hQueue), [checksInfo]);

    const isCanBlock = useMemo(
        () =>
            hasRole('orderUpdateSpecialMark') &&
            scoring &&
            (scoring.specialMark !== checksInfo?.scoring.specialMark ||
                scoring.specialMarkComment !== checksInfo?.scoring.specialMarkComment),
        [scoring, checksInfo]
    );

    useEffect(() => {
        if (!checksInfo?.scoring) return;
        setDirty(!isEqual(scoring, checksInfo?.scoring));
    }, [scoring, checksInfo]);

    useEffect(() => {
        !wasOpened && setWasOpened(isOpen);
    }, [isOpen]);

    const reasons =
        hqueues?.hqueueTotal &&
        hqueues.hqueueTotal['ManualCheckReasons'] &&
        hqueues.hqueueTotal['ManualCheckReasons'].result
            ? Object.entries(
                  groupBy(
                      hqueues.hqueueTotal['ManualCheckReasons'].result
                          .split(';')
                          .filter((s) => s.includes('=')),
                      (s) => s.split('=')[0]
                  )
              ).reduce<Dictionary<string[]>>((res, [key, value]) => {
                  res[key] = res[key]
                      ? [...res[key], value[0].split('=')[1]]
                      : [value[0].split('=')[1]];
                  return res;
              }, {})
            : undefined;

    const checkReason = (value: string) => {
        return !!(reasons && reasons[value]) || false;
    };

    const ScrField = ({
        title,
        values,
        hqueue,
    }: {
        title?: string;
        values: { key: string; label: string }[];
        hqueue: Dictionary<OrderScoringHQueueParam>;
    }) =>
        values.every((x) => !!hqueue[x.key]) ? (
            <>
                {title && <h6 className="card-subtitle text-info">{title}</h6>}
                <Row>
                    {values.map((x) => (
                        <Col key={x.key}>
                            <Input
                                value={hqueue[x.key].result}
                                label={t(x.label)}
                                invalid={checkReason(x.key)}
                                invalidMessage={reasons && reasons[x.key]?.join(';')}
                                disabled
                            />
                        </Col>
                    ))}
                </Row>
            </>
        ) : (
            <></>
        );

    const InputOptions = ({
        optionList,
        defaultValue,
    }: {
        optionList: DropDown[] | null;
        defaultValue?: string;
    }) => (
        <>
            {defaultValue && <option value="">{t(defaultValue)}</option>}
            {optionList && GetOptions(optionList)}
        </>
    );

    const updateFinalDecision = (checkInfo: Partial<OrderPvChecks>) => {
        setScoring((prev) =>
            prev
                ? {
                      ...prev,
                      totalCheck: prev?.totalCheck
                          ? { ...prev?.totalCheck, ...checkInfo }
                          : (checkInfo as OrderPvChecks),
                  }
                : prev
        );
    };

    return {
        checksInfo,
        checksInfoFetching,
        dictsFetching,
        hqueues,
        InputOptions,
        isCanBlock,
        pvTotalCheckDict,
        reasons,
        reasonsForDeclinedDict,
        reasonsForDeclinedLvl2Dict,
        reasonsForApproveDict,
        reasonsForApproveLvl2Dict,
        checkTabDictInfo,
        ScrField,
        specialMarkMutation,
        updateFinalDecision,
        isRepeatClient,
        isRepeatClientFetching,
    };
};

export default useChecksTabHelper;
